import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  Alert,
  Button,
} from '@mui/material';

import { SetContextHelpModalProps } from '../types';

export const SetContextHelpModal: React.FC<SetContextHelpModalProps> = ({ open, onClose }) => (
  <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
    <DialogTitle>Set Context - Complete Guide</DialogTitle>
    <DialogContent dividers>
      <Typography variant="h6" gutterBottom sx={{ mt: 1 }}>
        Overview
      </Typography>
      <Typography paragraph>
        The Set Context action updates context values directly, allowing you to store data that can
        be used by other actions in your preset. Context acts as a shared data store throughout the
        conversation.
      </Typography>

      <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
        Context Paths &amp; Nested Values
      </Typography>
      <Typography paragraph>
        Use dot notation to create nested structures and organize your data:
      </Typography>
      <Paper
        sx={{ p: 2, mb: 2, bgcolor: 'action.hover', border: '1px solid', borderColor: 'divider' }}
      >
        <Typography variant="body2" component="pre" sx={{ fontFamily: 'monospace' }}>
          {`campaignType: "annual"
user.preferences.theme: "dark"
settings.notifications.email: true
metadata.campaign.created: "2024-01-15"`}
        </Typography>
      </Paper>

      <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
        Using Context in Other Actions
      </Typography>
      <Typography paragraph>
        Once set, context values can be accessed in other actions using template variables or
        context references:
      </Typography>

      <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
        Template Variables (in text fields):
      </Typography>
      <Paper
        sx={{ p: 2, mb: 2, bgcolor: 'action.hover', border: '1px solid', borderColor: 'divider' }}
      >
        <Typography variant="body2" component="pre" sx={{ fontFamily: 'monospace' }}>
          {`{{campaignType}}  →  "annual"
{{user.preferences.theme}}  →  "dark"
{{metadata.campaign.created}}  →  "2024-01-15"`}
        </Typography>
      </Paper>

      <Typography variant="subtitle2" gutterBottom>
        Context References (in method payloads):
      </Typography>
      <Paper
        sx={{ p: 2, mb: 2, bgcolor: 'action.hover', border: '1px solid', borderColor: 'divider' }}
      >
        <Typography variant="body2" component="pre" sx={{ fontFamily: 'monospace' }}>
          {`$context:campaignType
$context:user.preferences.theme
$context:settings.notifications`}
        </Typography>
      </Paper>

      <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
        Advanced: JSONPath Support
      </Typography>
      <Typography paragraph>
        For complex data manipulation, context lookups support JSONPath-plus syntax for advanced
        queries:
      </Typography>
      <List dense>
        <ListItem>
          <ListItemText primary="Array Filtering" secondary="$context:users[?(@.active == true)]" />
        </ListItem>
        <ListItem>
          <ListItemText primary="Array Mapping" secondary="$context:campaigns[*].title" />
        </ListItem>
        <ListItem>
          <ListItemText
            primary="Conditional Access"
            secondary="$context:config[?(@.environment == 'production')]"
          />
        </ListItem>
        <ListItem>
          <ListItemText
            primary="Deep Searches"
            secondary="$context:..nonprofits[?(@.verified == true)]"
          />
        </ListItem>
      </List>

      <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
        Common Patterns
      </Typography>

      <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
        User Preferences:
      </Typography>
      <Paper
        sx={{ p: 2, mb: 2, bgcolor: 'action.hover', border: '1px solid', borderColor: 'divider' }}
      >
        <Typography variant="body2" component="pre" sx={{ fontFamily: 'monospace' }}>
          {`user.name: "John Doe"
user.preferences.language: "en"
user.preferences.currency: "USD"
user.settings.notifications: true`}
        </Typography>
      </Paper>

      <Typography variant="subtitle2" gutterBottom>
        Campaign Configuration:
      </Typography>
      <Paper
        sx={{ p: 2, mb: 2, bgcolor: 'action.hover', border: '1px solid', borderColor: 'divider' }}
      >
        <Typography variant="body2" component="pre" sx={{ fontFamily: 'monospace' }}>
          {`campaign.type: "fundraiser"
campaign.goal: 10000
campaign.currency: "USD"
campaign.nonprofits: ["12345", "67890"]`}
        </Typography>
      </Paper>

      <Typography variant="subtitle2" gutterBottom>
        Generated Content Storage:
      </Typography>
      <Paper
        sx={{ p: 2, mb: 2, bgcolor: 'action.hover', border: '1px solid', borderColor: 'divider' }}
      >
        <Typography variant="body2" component="pre" sx={{ fontFamily: 'monospace' }}>
          {`generated.title: "Help Education in San Francisco"
generated.description: "Join us in making a difference..."
generated.cta: "Donate Now to Support Education"`}
        </Typography>
      </Paper>

      <Alert severity="info" sx={{ mt: 3 }}>
        <Typography variant="body2">
          <strong>💡 Pro Tip:</strong> Use structured context paths to organize your data logically.
          This makes it easier to reference values in templates and debug your presets.
        </Typography>
      </Alert>
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose} variant="contained">
        Got it!
      </Button>
    </DialogActions>
  </Dialog>
);
